// WP-1.9 (S-16 `/tpf`, M10 TP-1): case cards — owner party, held amount, owed-back, status.
import { CURRENCIES, type CurrencyCode } from '@pfos/engine';
import { Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { EmptyState } from '../components/EmptyState';
import { Field, inputClass, selectClass } from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { ApiError } from '../lib/api';
import { useCreateTpfCase, useTpfCases } from '../lib/tpf';

function CreateTpfCaseForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [party, setParty] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateTpfCase();

  useEffect(() => {
    if (open) {
      setParty('');
      setCurrency('PKR');
      setError(null);
    }
  }, [open]);

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New third-party case"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (party.trim() === '') {
          setError('Party is required');
          return;
        }
        createMutation.mutate(
          { party: party.trim(), currency },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      <Field label="Party" htmlFor="tpf-case-party">
        <input
          id="tpf-case-party"
          className={inputClass}
          value={party}
          onChange={(e) => {
            setParty(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency" htmlFor="tpf-case-currency">
        <select
          id="tpf-case-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {Object.keys(CURRENCIES).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}

export function TpfPage(): React.JSX.Element {
  const { data: cases, isLoading } = useTpfCases();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Third-party</h1>
        <button
          type="button"
          className="rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          New case
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (cases === undefined || cases.length === 0) && (
        <EmptyState
          icon={Users}
          message="No third-party cases yet."
          action={{
            label: 'New case',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {cases !== undefined && cases.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {cases.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                navigate(`/tpf/${c.id}`);
              }}
              className="rounded-[var(--radius-card)] border border-border bg-surface p-4 text-left hover:bg-bg"
            >
              <p className="text-sm font-medium text-ink">{c.party}</p>
              <Amount
                currency={c.currency}
                minorUnits={c.owedBackMinor}
                className="text-lg font-semibold"
              />
              <p className="mt-1 text-xs capitalize text-muted">Owed back · {c.status}</p>
            </button>
          ))}
        </div>
      )}

      <CreateTpfCaseForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
